/*
 * Free gift threshold
 * -------------------
 * Keeps a gift variant in the cart whenever the qualifying spend is at or above a
 * threshold, and takes it back out when the cart drops below it. The price itself is
 * handled by an automatic "Buy X get Y" discount in Shopify — this file only manages
 * the line item, so if the JS never runs the customer is simply never charged wrongly.
 *
 * Config comes from snippets/free-gift.liquid as window.WVFreeGift.
 */
(function () {
  const config = window.WVFreeGift;
  if (!config) return;

  const FREE_ITEM_LIMIT = Number(config.freeItemLimit) || 0; // 0 = no cap
  const giftEnabled = !!(config.enabled && config.variantId && config.threshold);

  // The free-item cap is useful on its own, so only bail out if there is nothing to do.
  if (!giftEnabled && !FREE_ITEM_LIMIT) return;

  const VARIANT_ID = Number(config.variantId);
  const THRESHOLD = Number(config.threshold); // cents
  const DISMISSED_KEY = 'wv:free-gift-dismissed';
  const UNAVAILABLE_KEY = 'wv:free-gift-unavailable';
  const REMEMBERED_KEY = 'wv:free-gift-remembered-variant';
  const INTERNAL_HEADER = 'X-WV-Free-Gift';

  let syncing = false;
  let pending = false;
  let wokenByCartWrite = false;
  let syncTimer = null;
  let lastGiftQuantity = null;
  let lastFreeSnapshot = null; // line key -> quantity, as of the previous pass

  /* ------------------------------------------------------------------ helpers */

  const GIFT_IDS = Array.isArray(config.giftVariantIds) && config.giftVariantIds.length
    ? config.giftVariantIds.map(Number)
    : [VARIANT_ID];
  // Any birth-month variant counts as the gift. VARIANT_ID is only which one to ADD.
  const isGift = (item) => GIFT_IDS.indexOf(item.variant_id) !== -1;

  // Variants Shopify leaves out of the qualifying collection — currently the New Moon
  // gift necklace, which is excluded by name so it cannot satisfy its own threshold.
  // This list has to track that collection: if the two disagree, we add a gift line the
  // discount will not zero, the fail-safe below pulls it back out, and the shopper is
  // locked out of the offer for the rest of the session.
  const EXCLUDED = (config.excludedVariantIds || []).map(Number);

  const countsTowardThreshold = (item) =>
    !isGift(item) && EXCLUDED.indexOf(item.variant_id) === -1;

  // Spend that counts toward the threshold, in cents, ignoring the gift itself and
  // using pre-discount prices so it matches what the offer promises the shopper.
  function qualifyingSpend(cart) {
    return cart.items.reduce(
      (total, item) => (countsTowardThreshold(item) ? total + item.original_line_price : total),
      0
    );
  }

  // The gift belongs to the birthstone welcome funnel, not to all traffic. Only the
  // people carrying the code can actually have it zeroed, so managing the line for
  // anyone else means adding a necklace, failing to discount it, and yanking it back
  // out -- and worse, deleting the necklace of someone buying it deliberately at $39.
  function inFunnel(cart) {
    // The discount code is the entitlement. A remembered birth month only says which
    // variant to add; it does not make the gift free. Adding the necklace for someone
    // without the code means it comes back at full price and gets pulled straight out
    // again -- churn the shopper sees, for a gift they were never going to get.
    const code = config.giftCode;
    const codes = (cart && cart.discount_codes) || [];
    for (let i = 0; i < codes.length; i++) {
      if (code && codes[i] && codes[i].code === code) return true;
    }
    return false;
  }

  function giftLine(cart) {
    const index = cart.items.findIndex(isGift);
    return index === -1 ? null : { index: index + 1, item: cart.items[index] };
  }

  function dismissed() {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDismissed(value) {
    try {
      if (value) sessionStorage.setItem(DISMISSED_KEY, '1');
      else sessionStorage.removeItem(DISMISSED_KEY);
    } catch (e) {
      /* private mode — fall back to re-adding, which is the safer default */
    }
  }

  // Remembers which birth-month variant was just pulled back out below the threshold
  // (e.g. the necklace the welcome email added, removed here because nothing else was
  // in the cart yet), so addGift() can restore that same variant once the shopper
  // crosses the threshold instead of always falling back to the site-wide default.
  function rememberedVariant() {
    try {
      const id = Number(sessionStorage.getItem(REMEMBERED_KEY));
      return GIFT_IDS.indexOf(id) !== -1 ? id : null;
    } catch (e) {
      return null;
    }
  }

  function setRememberedVariant(id) {
    try {
      sessionStorage.setItem(REMEMBERED_KEY, String(id));
    } catch (e) {
      /* private mode — falls back to the default variant, same as before this fix */
    }
  }

  // Set when the gift came back priced above zero, meaning the automatic discount did
  // not apply. Stops us re-adding a line the shopper would be charged for.
  // Records the qualifying spend at which the gift failed to zero, not just a flag.
  // The gift is zeroed by a discount CODE with its own minimum, so a line that came
  // back priced at $0 spend says nothing about the same line at $40 -- latching a
  // permanent "unavailable" there would strand the shopper for the whole session.
  function unavailable(spend) {
    try {
      const at = sessionStorage.getItem(UNAVAILABLE_KEY);
      if (at === null) return false;
      if (at === '1') return true; // legacy flag from an earlier session
      return Number(spend) <= Number(at);
    } catch (e) {
      return false;
    }
  }

  function setUnavailable(spend) {
    try {
      sessionStorage.setItem(UNAVAILABLE_KEY, String(Number(spend) || 0));
    } catch (e) {
      /* nothing to do — the guard below still removes the line this time round */
    }
  }

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [INTERNAL_HEADER]: '1',
      },
      body: JSON.stringify(body),
    });
  }

  /* ------------------------------------------------------------- ui rendering */

  function cartDrawerSectionId() {
    const drawer = document.querySelector('cart-drawer');
    const wrapper = drawer && drawer.closest('.shopify-section');
    return wrapper && wrapper.id ? wrapper.id.replace(/^shopify-section-/, '') : null;
  }

  // Mirrors the section ids/selectors the theme's own cart components render.
  function sectionsToRender() {
    const sections = [];
    const drawerId = cartDrawerSectionId();

    if (drawerId && document.querySelector('#CartDrawer')) {
      sections.push({ id: drawerId, target: '#CartDrawer', selector: '#CartDrawer' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble', target: '#cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.querySelector('cart-items .js-contents')) {
      sections.push({ id: 'main-cart-items', target: 'cart-items .js-contents', selector: '.js-contents' });
    }
    if (document.querySelector('.cart__footer .js-contents')) {
      sections.push({ id: 'main-cart-footer', target: '.cart__footer .js-contents', selector: '.js-contents' });
    }
    return sections;
  }

  // Re-render the cart markup from the server without changing the cart. Needed when the
  // gift state is already correct but the rendered progress message is not — e.g. the
  // shopper removed the gift on the cart page, which leaves the drawer's copy stale.
  async function refreshSections(sections) {
    if (!sections.length) return;

    try {
      const url =
        window.location.pathname + '?sections=' + sections.map((section) => section.id).join(',');
      const rendered = await fetch(url, { headers: { [INTERNAL_HEADER]: '1' } }).then((response) =>
        response.json()
      );
      applySections(sections, rendered);
    } catch (error) {
      /* stale copy is cosmetic — never surface this */
    }
  }

  function applySections(sections, rendered) {
    if (!rendered) return;

    sections.forEach((section) => {
      const html = rendered[section.id];
      const element = document.querySelector(section.target);
      if (!html || !element) return;

      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (source) element.innerHTML = source.innerHTML;
    });
  }

  /* -------------------------------------------------------------- cart writes */

  async function addGift(sections) {
    // Prefer the shopper's own birth-month variant if we pulled one back out earlier
    // this session (see setRememberedVariant below); only fall back to the site-wide
    // default when we never saw one, e.g. this is their first time crossing the
    // threshold and the gift was never added or removed before.
    const targetVariant = rememberedVariant() || VARIANT_ID;
    const response = await request(window.routes.cart_add_url + '.js', {
      items: [
        {
          id: targetVariant,
          quantity: 1,
          properties: config.lineProperty ? { [config.lineProperty]: config.lineValue } : undefined,
        },
      ],
      sections: sections.map((section) => section.id),
      sections_url: window.location.pathname,
    });

    if (!response.ok) return null; // out of stock, unpublished, etc. — leave the cart alone
    return response.json();
  }

  async function setGiftQuantity(line, quantity, sections) {
    const response = await request(window.routes.cart_change_url + '.js', {
      line: line,
      quantity: quantity,
      sections: sections.map((section) => section.id),
      sections_url: window.location.pathname,
    });

    if (!response.ok) return null;
    return response.json();
  }

  // Addressed by line key rather than index, because reducing a line to zero reindexes
  // everything after it and we may be adjusting several lines in one pass.
  async function setQuantityByKey(key, quantity, sections) {
    const response = await request(window.routes.cart_change_url + '.js', {
      id: key,
      quantity: quantity,
      sections: sections.map((section) => section.id),
      sections_url: window.location.pathname,
    });

    if (!response.ok) return null;
    return response.json();
  }

  /* ------------------------------------------------------- free item cap */

  // Free items are $0 products the shopper claimed. The gift-with-purchase line is a
  // full-price product zeroed by a discount, so it is deliberately not counted here.
  function freeItemTotal(cart) {
    return cart.items.reduce(
      (total, item) => (item.original_price === 0 ? total + item.quantity : total),
      0
    );
  }

  function snapshotFreeLines(cart) {
    lastFreeSnapshot = {};
    cart.items.forEach((item) => {
      if (item.original_price === 0) lastFreeSnapshot[item.key] = item.quantity;
    });
  }

  // Brings the cart back inside the cap, keeping what the shopper just chose.
  //
  // Priority is: lines whose quantity just went up (they were picked a moment ago, on
  // this page or the free offer page they came from), then the newest lines. Whatever
  // is left over comes off the oldest picks. So claiming 1 piece on a second free offer
  // while holding 5 of the first leaves 4 + 1, rather than silently dropping the thing
  // they just asked for.
  async function enforceFreeItemCap(cart, sections) {
    if (!FREE_ITEM_LIMIT) return null;
    if (freeItemTotal(cart) <= FREE_ITEM_LIMIT) return null;

    const freeLines = [];
    cart.items.forEach((item, index) => {
      if (item.original_price !== 0) return;
      const before = lastFreeSnapshot ? lastFreeSnapshot[item.key] || 0 : 0;
      freeLines.push({ item: item, index: index, grew: lastFreeSnapshot ? item.quantity > before : false });
    });

    // Shopify returns cart lines newest first, so a lower index means a more recent pick.
    const newestFirst = (a, b) => a.index - b.index;
    const priority = freeLines
      .filter((entry) => entry.grew)
      .sort(newestFirst)
      .concat(freeLines.filter((entry) => !entry.grew).sort(newestFirst));

    let allowance = FREE_ITEM_LIMIT;
    let result = null;

    for (const entry of priority) {
      const keep = Math.min(entry.item.quantity, allowance);
      allowance -= keep;

      if (keep === entry.item.quantity) continue;

      const response = await setQuantityByKey(entry.item.key, keep, sections);
      if (!response) break;
      result = response;
    }

    return result;
  }

  /* ------------------------------------------------------------------- reconcile */

  async function reconcile() {
    // Cart writes can land faster than a pass takes. Queue the latest one instead of
    // dropping it, or a quick second click leaves the gift out of step with the cart.
    if (syncing) {
      pending = true;
      return;
    }
    syncing = true;

    const afterCartWrite = wokenByCartWrite;
    wokenByCartWrite = false;

    try {
      let cart = await fetch(window.routes.cart_url + '.js', {
        headers: { [INTERNAL_HEADER]: '1' },
      }).then((response) => response.json());

      const sectionsForCap = sectionsToRender();
      const capResult = await enforceFreeItemCap(cart, sectionsForCap);

      if (capResult) {
        cart = await fetch(window.routes.cart_url + '.js', {
          headers: { [INTERNAL_HEADER]: '1' },
        }).then((response) => response.json());
        applySections(sectionsForCap, capResult.sections);
      }

      snapshotFreeLines(cart);

      const sections = sectionsToRender();
      const funnel = inFunnel(cart);
      const eligible = giftEnabled && funnel && qualifyingSpend(cart) >= THRESHOLD;
      const gift = giftEnabled && funnel ? giftLine(cart) : null;
      let adding = false;
      let result = null;

      if (giftEnabled && funnel) {
        // Shopper pulled the gift out themselves: respect it until they drop below the
        // threshold again, otherwise the line reappears the moment they remove it.
        if (eligible && !gift && lastGiftQuantity > 0) setDismissed(true);
        if (!eligible) setDismissed(false);

        lastGiftQuantity = gift ? gift.item.quantity : 0;
        adding = eligible && !gift && !dismissed() && !unavailable(qualifyingSpend(cart));

        if (adding) {
          result = await addGift(sections);
        } else if (eligible && gift && gift.item.quantity > 1) {
          result = await setGiftQuantity(gift.index, 1, sections);
        } else if (!eligible && gift) {
          // Below the threshold the gift STAYS. The welcome email puts the necklace in
          // the cart deliberately, and pulling it straight back out made it flicker in
          // and vanish. It simply shows at its own price until the cart qualifies, and
          // buying it alone at $39 is a legitimate order rather than something to
          // prevent. Its variant is still remembered so the notice can name the stone.
          setRememberedVariant(gift.item.variant_id);
        }
      }

      if (!result && !capResult && afterCartWrite && sections.length) {
        // Nothing changed, but the rendered notices may be describing an older cart —
        // or may not be on the page at all yet, if the cart was empty at page load.
        await refreshSections(sections);
      }

      if (result) {
        const updated = await fetch(window.routes.cart_url + '.js', {
          headers: { [INTERNAL_HEADER]: '1' },
        }).then((response) => response.json());
        const updatedGift = giftLine(updated);
        lastGiftQuantity = updatedGift ? updatedGift.item.quantity : 0;

        // Fail-safe: the automatic discount must zero this line. If it did not — the
        // discount is paused, expired, or scoped differently — pull the gift back out.
        // Never leave a charged line in a cart the shopper did not put it in.
        if (adding && updatedGift && updatedGift.item.final_line_price > 0) {
          setUnavailable(qualifyingSpend(cart));
          const removal = await setGiftQuantity(updatedGift.index, 0, sections);
          lastGiftQuantity = 0;
          if (removal) result = removal;
        }

        applySections(sections, result.sections);
      }
    } catch (error) {
      /* never let gift logic break the cart */
    } finally {
      syncing = false;
      if (pending) {
        pending = false;
        scheduleReconcile();
      }
    }
  }

  function scheduleReconcile() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(reconcile, 150);
  }

  /* ---------------------------------------------------------------- listeners */

  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  const originalFetch = window.fetch;
  window.fetch = function (resource, options) {
    const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
    const headers = (options && options.headers) || {};
    const internal = headers[INTERNAL_HEADER] || (headers.get && headers.get(INTERNAL_HEADER));
    const result = originalFetch.apply(this, arguments);

    if (!internal && CART_WRITE.test(url)) {
      result.then((response) => {
        if (response && response.ok) {
          wokenByCartWrite = true;
          scheduleReconcile();
        }
      }).catch(() => {});
    }

    return result;
  };

  // "Add my free gift" — shown when the shopper is over the threshold but the gift is
  // not in the cart. Clears the removal flag, since asking for it is a change of mind.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-free-gift-add]');
    if (!button) return;

    event.preventDefault();
    button.setAttribute('disabled', 'disabled');
    setDismissed(false);

    const sections = sectionsToRender();
    const result = await addGift(sections);

    if (result) {
      applySections(sections, result.sections);
    } else {
      button.removeAttribute('disabled');
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile);
  } else {
    scheduleReconcile();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleReconcile();
  });
})();
